import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useAuth } from '@/features/auth';
import { isValidUUID } from '@/lib/utils';
import type { BabyName } from '../types';

interface LikedName extends BabyName {
  swipe_id: string;
  swipe_type: 'like' | 'favorite';
  swiped_at: string;
}

export function useLikes() {
  const { user } = useAuth();
  const { currentPregnancy, lastCompletedPregnancy, appMode } = usePregnancyStore();
  const [likes, setLikes] = useState<LikedName[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'like' | 'favorite'>('all');

  // In baby mode, use lastCompletedPregnancy; otherwise use currentPregnancy
  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';
  const rawPregnancyId = currentPregnancy?.id || (isBabyMode ? lastCompletedPregnancy?.id : null);

  // Validate that the pregnancy ID is a valid UUID before using it
  const activePregnancyId = isValidUUID(rawPregnancyId) ? rawPregnancyId : null;

  // Fetch user's likes
  const fetchLikes = useCallback(async () => {
    if (!activePregnancyId || !user) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('name_swipes')
        .select(`
          id,
          swipe_type,
          created_at,
          baby_name:baby_names(*)
        `)
        .eq('pregnancy_id', activePregnancyId)
        .eq('user_id', user.id)
        .in('swipe_type', ['like', 'favorite'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform data - baby_name is returned as object from single relation
      const transformedLikes: LikedName[] = (data || [])
        .filter(item => item.baby_name)
        .map(item => {
          const babyName = item.baby_name as unknown as BabyName;
          return {
            ...babyName,
            swipe_id: item.id,
            swipe_type: item.swipe_type as 'like' | 'favorite',
            swiped_at: item.created_at,
          };
        });

      setLikes(transformedLikes);
    } catch (err) {
      console.error('Error fetching likes:', err);
    } finally {
      setIsLoading(false);
    }
  }, [activePregnancyId, user]);

  // Remove a like (put name back in pool)
  const removeLike = useCallback(async (swipeId: string) => {
    try {
      // Delete the swipe record
      const { error } = await supabase
        .from('name_swipes')
        .delete()
        .eq('id', swipeId);

      if (error) throw error;

      // Update local state
      setLikes(prev => prev.filter(like => like.swipe_id !== swipeId));

      return true;
    } catch (err) {
      console.error('Error removing like:', err);
      return false;
    }
  }, []);

  // Change like to favorite or vice versa
  const toggleFavorite = useCallback(async (swipeId: string, currentType: 'like' | 'favorite') => {
    const newType = currentType === 'favorite' ? 'like' : 'favorite';

    try {
      const { error } = await supabase
        .from('name_swipes')
        .update({ swipe_type: newType })
        .eq('id', swipeId);

      if (error) throw error;

      // Update local state
      setLikes(prev => prev.map(like =>
        like.swipe_id === swipeId
          ? { ...like, swipe_type: newType }
          : like
      ));

      return true;
    } catch (err) {
      console.error('Error toggling favorite:', err);
      return false;
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchLikes();
  }, [fetchLikes]);

  // Filter likes
  const filteredLikes = likes.filter(like => {
    if (filter === 'all') return true;
    return like.swipe_type === filter;
  });

  // Stats
  const stats = {
    total: likes.length,
    likes: likes.filter(l => l.swipe_type === 'like').length,
    favorites: likes.filter(l => l.swipe_type === 'favorite').length,
  };

  return {
    likes: filteredLikes,
    isLoading,
    filter,
    setFilter,
    stats,
    removeLike,
    toggleFavorite,
    refetch: fetchLikes,
  };
}
